import React from "react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { ArrowRight, Split } from "lucide-react";
import {
  conversionGap,
  painPointInsights,
  painPointIntro,
} from "./pain-points";

const easeOut = [0.22, 1, 0.36, 1] as const;

const leftContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.04 },
  },
};

const leftChild = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: easeOut },
  },
};

export default function PainPointsSection() {
  return (
    <section id="pain-points" className="relative w-full">
      <div className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-28 lg:px-8 lg:py-36">
      <LazyMotion features={domAnimation}>
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-14 xl:gap-16">
          {/* Left: narrative */}
          <m.div
            className="flex min-w-0 flex-col"
            variants={leftContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
          >
            <m.p
              variants={leftChild}
              className="text-[13px] font-medium tracking-wide text-[#00b7ff] sm:text-sm"
            >
              {painPointIntro.kicker}
            </m.p>

            <m.h2
              variants={leftChild}
              className="text-foreground mt-2 max-w-[18ch] text-balance text-[1.65rem] font-bold leading-[1.25] tracking-tight sm:mt-3 sm:text-4xl sm:leading-[1.2]"
            >
              {painPointIntro.title}
            </m.h2>

            <m.p
              variants={leftChild}
              className="text-default-500 mt-4 max-w-[42ch] text-pretty text-[15px] leading-relaxed sm:mt-5 sm:text-base"
            >
              {painPointIntro.body}
            </m.p>

            <m.ul
              variants={leftChild}
              className="mt-9 flex flex-col sm:mt-11"
              role="list"
            >
              {painPointInsights.map((item) => (
                <li
                  key={item.index}
                  className="border-default-100/50 flex gap-4 border-t py-5 first:border-t last:pb-0 sm:gap-5 sm:py-6"
                >
                  <span className="text-primary shrink-0 font-mono text-[13px] font-medium tabular-nums tracking-wide sm:text-sm">
                    {item.index}
                  </span>
                  <div className="min-w-0">
                    <p className="text-foreground text-[15px] font-semibold tracking-tight sm:text-base">
                      {item.title}
                    </p>
                    <p className="text-default-500 mt-1.5 text-sm leading-relaxed sm:text-[15px]">
                      {item.body}
                    </p>
                  </div>
                </li>
              ))}
            </m.ul>
          </m.div>

          {/* Right: conversion gap panel */}
          <m.aside
            className="border-default-100/60 relative min-w-0 overflow-hidden rounded-2xl border bg-[#08080c] shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_28px_80px_-40px_rgba(0,0,0,0.85)]"
            initial={{ opacity: 0, y: 28, scale: 0.985 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{ duration: 0.62, ease: easeOut, delay: 0.08 }}
            aria-labelledby="conversion-gap-title"
          >
            <div className="relative border-b border-white/[0.08] bg-[#0c0c10] px-5 py-5 sm:px-6 sm:py-6">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00b7ff]/40 to-transparent" />
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[11px] font-medium tracking-[0.16em] text-zinc-300 uppercase">
                    {conversionGap.label}
                  </p>
                  <h3
                    id="conversion-gap-title"
                    className="mt-2 text-balance text-lg font-semibold tracking-tight text-white sm:text-xl"
                  >
                    {conversionGap.title}
                  </h3>
                </div>
                <Split
                  className="mt-0.5 size-5 shrink-0 text-white/70"
                  strokeWidth={1.5}
                  aria-hidden
                />
              </div>
            </div>

            <ol className="divide-y divide-white/[0.08] bg-[#0a0a0e] px-1 sm:px-2" role="list">
              {conversionGap.rows.map((row, i) => (
                <m.li
                  key={row.index}
                  className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 px-4 py-3.5 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-baseline sm:gap-x-4 sm:px-4 sm:py-4"
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-5% 0px" }}
                  transition={{
                    delay: 0.12 + i * 0.045,
                    duration: 0.4,
                    ease: easeOut,
                  }}
                >
                  <span className="font-mono text-[12px] tabular-nums text-zinc-400 sm:text-[13px]">
                    {row.index}
                  </span>
                  <p className="min-w-0 text-[13px] leading-snug text-zinc-100 sm:text-sm">
                    {row.cause}
                  </p>
                  <span
                    className={`col-start-2 text-[12px] font-medium tracking-tight sm:col-start-auto sm:justify-self-end sm:text-right sm:text-[13px] ${
                      row.tone === "resolve"
                        ? "text-emerald-400"
                        : "text-rose-400"
                    }`}
                  >
                    {row.impact}
                  </span>
                </m.li>
              ))}
            </ol>

            <div className="border-t border-emerald-400/20 bg-emerald-500/[0.12] px-5 py-4 sm:px-6 sm:py-5">
              <p className="flex items-start gap-2.5 text-[13px] leading-relaxed text-emerald-100 sm:text-sm">
                <ArrowRight
                  className="mt-0.5 size-4 shrink-0 text-emerald-400"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <span>{conversionGap.footer}</span>
              </p>
            </div>
          </m.aside>
        </div>
      </LazyMotion>
      </div>
    </section>
  );
}
